import { __rest, __assign } from 'tslib';
import * as React from 'react';
import { classNames, analyticsTracker } from 'uitk-react-utils';
import { UitkLinkSizes, UitkLink } from 'uitk-react-link';
import { UitkIcon } from 'uitk-react-icons';
import { UitkExpandoTriggerStates } from './types.esm.js';
import styles from './uitk-expando.css';

var getClassNameList = classNames.bind(styles);
/**
 * This component contains a details disclosure which when clicked will expand and show a
 * hidden description. Expansion is determined by the boolean value of {@code isVisible}.
 *
 * @param className the provided classes to be used in both the title and the description
 *
 * In the case that the user disabled JavaScript, the details disclosure default behaviour
 * will be active. This is akin to the JavaScript enabled behaviour.
 */
var getTestId = function (testId, isOpen) { return (isOpen ? "".concat(testId, "-expanded") : "".concat(testId, "-collapsed")); };
var UitkExpando = function (_a) {
    var _b;
    var analytics = _a.analytics, alignment = _a.alignment, expandoDescription = _a.expandoDescription, expandoTitle = _a.expandoTitle, className = _a.className, children = _a.children, isVisible = _a.isVisible, isDisabled = _a.isDisabled, _c = _a.isIndented, isIndented = _c === void 0 ? true : _c, onToggle = _a.onToggle, triggerPosition = _a.triggerPosition, type = _a.type, _d = _a.size, size = _d === void 0 ? UitkLinkSizes.get('small') : _d, attrs = __rest(_a, ["analytics", "alignment", "expandoDescription", "expandoTitle", "className", "children", "isVisible", "isDisabled", "isIndented", "onToggle", "triggerPosition", "type", "size"]);
    var localAnalyticsId = isVisible
        ? UitkExpandoTriggerStates.get('collapse')
        : UitkExpandoTriggerStates.get('expand');
    var onToggleHandler = function (event) {
        var _a;
        if (((_a = event === null || event === void 0 ? void 0 : event.currentTarget) === null || _a === void 0 ? void 0 : _a.open) !== isVisible) {
            onToggle === null || onToggle === void 0 ? void 0 : onToggle();
            var expandoAnalytics = analytics && __assign({ localAnalyticsId: localAnalyticsId }, analytics);
            expandoAnalytics && analyticsTracker(expandoAnalytics);
        }
    };
    var onLinkToggleHandler = function () {
        onToggle === null || onToggle === void 0 ? void 0 : onToggle();
        var expandoAnalytics = analytics && __assign({ localAnalyticsId: localAnalyticsId }, analytics);
        expandoAnalytics && analyticsTracker(expandoAnalytics);
    };
    var expandoTriggerIcon = (React.createElement(UitkIcon, { className: "uitk-expando-trigger-icon".concat(isVisible ? ' uitk-expando-trigger-icon-active' : ''), "data-testid": isVisible ? 'uitk-expando-button-icon-expanded' : undefined, name: "expand_more" }));
    var title = (React.createElement(React.Fragment, null,
        triggerPosition === 'leading' && expandoTriggerIcon,
        React.createElement("span", { className: getClassNameList('uitk-expando-title') }, expandoTitle),
        triggerPosition === 'trailing' && expandoTriggerIcon));
    return (React.createElement("details", __assign({ open: isDisabled ? !isDisabled : isVisible, onToggle: onToggleHandler, className: getClassNameList('uitk-expando', "uitk-expando-".concat(type === 'list' ? 'list-item' : type), className, {
            'is-disabled': isDisabled
        }) }, attrs),
        React.createElement("summary", { className: getClassNameList('uitk-expando-trigger-content') }, type === 'link' ? (React.createElement(UitkLink, { size: size, className: getClassNameList('uitk-expando-link'), align: alignment === 'trailing' ? 'right' : undefined },
            React.createElement("button", { type: "button", onClick: onLinkToggleHandler }, title))) : (title)),
        React.createElement("div", { className: getClassNameList('uitk-expando-body', (_b = {}, _b["is-indented-".concat(triggerPosition)] = isIndented, _b)), "data-testid": getTestId("uitk-expando-".concat(type, "-body"), isVisible) },
            React.createElement("div", { className: getClassNameList('uitk-expando-description') }, expandoDescription !== null && expandoDescription !== void 0 ? expandoDescription : children))));
};
UitkExpando.displayName = 'UitkExpando';

export { UitkExpando };
